import { HOME_WIDGET_QUERY_META } from "@/lib/home/widgets/query-lifecycle";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import type {
  AdminLinkQRDetail,
  AdminLinkQRListResponse,
  LinkQRListResponse,
  LinkQrSortField,
} from "@/lib/qr-codes/link-types";

/**
 * The user's link QR codes for a search/sort — shared by the Codes QR Liens
 * list and the home page (default: no search, latest change first).
 */
export function linkQrListQueryOptions(
  search = "",
  sort: LinkQrSortField = "lastModified",
  order: "asc" | "desc" = "desc",
) {
  return queryOptions({
    queryKey: ["link-qr-codes", search, sort, order],
    meta: HOME_WIDGET_QUERY_META,
    queryFn: ({ signal }) => fetchLinkQrList({ search, sort, order, page: 1, limit: 100, signal }),
  });
}

/** Rows per page of the Codes QR Liens list (pages load behind "Afficher plus"). */
export const LINK_QR_PAGE_SIZE = 50;

/**
 * The paged Codes QR Liens list for a search and sort. The list page and its
 * route loader share it, so the loader's warm read is the one the page mounts with.
 */
export function linkQrPagesQueryOptions({
  search = "",
  sort = "lastModified",
  order = "desc",
}: {
  search?: string;
  sort?: LinkQrSortField;
  order?: "asc" | "desc";
} = {}) {
  return infiniteQueryOptions({
    queryKey: ["link-qr-codes", "pages", search, sort, order],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchLinkQrList({ search, sort, order, page: pageParam, limit: LINK_QR_PAGE_SIZE, signal }),
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
  });
}

export async function fetchLinkQrList({
  search,
  sort,
  order,
  page,
  limit,
  signal,
}: {
  search: string;
  sort: LinkQrSortField;
  order: "asc" | "desc";
  page: number;
  limit: number;
  signal?: AbortSignal;
}): Promise<LinkQRListResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit), sort, order });

  if (search) params.set("search", search);
  const res = await fetch(`/api/link-qr-codes?${params}`, { cache: "no-store", signal });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  // SAFETY: the link-qr-codes route builds the { data, pagination } envelope.
  return (await res.json()) as LinkQRListResponse;
}

/** An admin's view of a link QR code with its full URL history and owner. */
export function adminLinkQrDetailQueryOptions(id: string, includeDeleted = false) {
  return queryOptions({
    queryKey: ["admin", "link-qr-codes", "detail", id, { includeDeleted }],
    queryFn: async (): Promise<AdminLinkQRDetail> => {
      const res = await fetch(
        `/api/admin/link-qr-codes/${id}${includeDeleted ? "?includeDeleted=true" : ""}`,
        { cache: "no-store" },
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: response shape is defined by GET /api/admin/link-qr-codes/[id].
      return (await res.json()) as AdminLinkQRDetail;
    },
  });
}

/** Rows per page of the admin link QR list: the whole organization in one or two pages; "Afficher plus" beyond that. */
export const ADMIN_LINK_QR_PAGE_SIZE = 100;

/** The admin's paged list of link QR codes (GET /api/admin/link-qr-codes). */
export function adminLinkQrListQueryOptions(params: {
  search: string;
  createdSince: string | null;
  sort: LinkQrSortField;
  order: "asc" | "desc";
}) {
  return infiniteQueryOptions({
    queryKey: [
      "admin",
      "link-qr-codes",
      "list",
      params.search,
      params.createdSince,
      params.sort,
      params.order,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<AdminLinkQRListResponse> => {
      const search = new URLSearchParams({
        page: String(pageParam),
        limit: String(ADMIN_LINK_QR_PAGE_SIZE),
        sort: params.sort,
        order: params.order,
      });

      if (params.search) search.set("search", params.search);

      if (params.createdSince) search.set("createdSince", params.createdSince);
      const res = await fetch(`/api/admin/link-qr-codes?${search}`, { cache: "no-store" });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: /api/admin/link-qr-codes returns the AdminLinkQRListResponse contract.
      return (await res.json()) as AdminLinkQRListResponse;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
  });
}
